// components/admin/accounting/PaymentsTable.jsx - Refactored and Clean
import React, { useState } from 'react';
import {
  DollarSign,
  CheckCircle,
  AlertCircle,
  Upload,
  Eye,
  Calendar,
  FileText,
  CreditCard
} from 'lucide-react';
import axios from 'axios';
import DepositadoModal from './modals/DepositadoModal';
import ImageUploadModal from './modals/ImageUploadModal';
import ImageViewModal from './modals/ImageViewModal';

const API_URL = process.env.REACT_APP_API_URL;

function PaymentsTable({ data, type, onUpdate }) {
  // Notes editing state
  const [editingNotes, setEditingNotes] = useState(null);
  const [notes, setNotes] = useState('');

  // Depositado modal state
  const [showDepositadoModal, setShowDepositadoModal] = useState(false);
  const [editingDepositado, setEditingDepositado] = useState(null);
  const [depositadoAmount, setDepositadoAmount] = useState('');

  // Image upload state
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Image view state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingImages, setViewingImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // ==================== NOTES HANDLERS ====================
  function handleNotesClick(item) {
    setEditingNotes(item.id);
    setNotes(item.notas || '');
  }

  function handleNotesSave(id) {
    onUpdate(id, 'notas', notes, type);
    setEditingNotes(null);
  }

  function handleNotesCancel() {
    setEditingNotes(null);
    setNotes('');
  }

  // ==================== DEPOSITADO HANDLERS ====================
  function handleDepositadoClick(item) {
    setEditingDepositado(item.id);
    setDepositadoAmount(item.depositado > 0 ? item.depositado : item.depositar);
    setShowDepositadoModal(true);
  }

  function handleDepositadoSave() {
    if (editingDepositado && depositadoAmount !== '') {
      const amount = parseFloat(depositadoAmount);
      if (!isNaN(amount) && amount >= 0) {
        onUpdate(editingDepositado, 'depositado', amount, type);
        setShowDepositadoModal(false);
        setEditingDepositado(null);
        setDepositadoAmount('');
      } else {
        alert('Por favor ingresa un monto válido');
      }
    }
  }

  function handleDepositadoCancel() {
    setShowDepositadoModal(false);
    setEditingDepositado(null);
    setDepositadoAmount('');
  }

  // ==================== IMAGE UPLOAD HANDLERS ====================
  function handleUploadClick(paymentId) {
    setCurrentPaymentId(paymentId);
    setShowImageModal(true);
    setSelectedFiles([]);
    setPreviewUrls([]);
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    addFiles(files);
  }

  function addFiles(files) {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      alert('Por favor selecciona solo archivos de imagen');
      return;
    }

    setSelectedFiles(prev => [...prev, ...imageFiles]);

    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  }

  async function handlePaste(e) {
    const items = e.clipboardData.items;
    const files = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        files.push(file);
      }
    }

    if (files.length > 0) {
      addFiles(files);
    }
  }

  function removeFile(index) {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  }

  async function handleUploadImages() {
    if (selectedFiles.length === 0) {
      alert('Por favor selecciona al menos una imagen');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('images', file);
      });

      await axios.post(
        `${API_URL}/api/accounting/payments/${currentPaymentId}/upload-images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Imágenes subidas exitosamente');
      handleCloseImageModal();

      if (onUpdate) {
        await onUpdate(currentPaymentId, 'refresh', null, type);
      }

    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error al subir las imágenes. Por favor intenta de nuevo.');
    } finally {
      setIsUploading(false);
    }
  }

  function handleCloseImageModal() {
    setShowImageModal(false);
    setCurrentPaymentId(null);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setDragActive(false);
  }

  // ==================== IMAGE VIEW HANDLERS ====================
  async function handleViewImages(paymentId) {
    setCurrentPaymentId(paymentId);
    setShowViewModal(true);
    setLoadingImages(true);

    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/payments/${paymentId}/images`
      );
      setViewingImages(response.data.images || []);
    } catch (error) {
      console.error('Error loading images:', error);
      alert('Error al cargar las imágenes');
    } finally {
      setLoadingImages(false);
    }
  }

  async function handleDeleteImage(imageId) {
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/accounting/payments/images/${imageId}`);

      setViewingImages(prev => prev.filter(img => img.id !== imageId));
      alert('Imagen eliminada exitosamente');

      if (onUpdate && currentPaymentId) {
        await onUpdate(currentPaymentId, 'refresh', null, type);
      }

    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error al eliminar la imagen');
    }
  }

  function handleCloseViewModal() {
    setShowViewModal(false);
    setCurrentPaymentId(null);
    setViewingImages([]);
  }

  // ==================== MISC HANDLERS ====================
  function handleToggleField(id, field, currentValue) {
    onUpdate(id, field, !currentValue, type);
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined) return 'S/ 0.00';
    return `S/ ${Number(amount).toFixed(2)}`;
  }

  // ==================== RENDER ====================
  if (!data || data.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
        <p className="text-slate-600 font-montserrat">
          No hay datos de pagos para mostrar en la fecha seleccionada
        </p>
      </div>
    );
  }

  function getTypeBadge(type) {
    const badges = {
      'user_lima': {
        bg: 'bg-brand-primary/10',
        text: 'text-brand-primary',
        label: 'Lima'
      },
      'user_provincia': {
        bg: 'bg-brand-gold/10',
        text: 'text-brand-gold',
        label: 'Provincia'
      },
      'provider_lima': {
        bg: 'bg-brand-primary/10',
        text: 'text-brand-primary',
        label: 'Prov Lima',
        icon: '🏪'
      },
      'provider_provincia': {
        bg: 'bg-brand-gold/10',
        text: 'text-brand-gold',
        label: 'Prov Prov',
        icon: '🏪'
      },
      'driver': {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        label: 'Driver'
      },
      'referral': {
        bg: 'bg-purple-100',
        text: 'text-purple-700',
        label: 'Referido'
      },
      // Legacy type for backwards compatibility
      'user': {
        bg: 'bg-brand-primary/10',
        text: 'text-brand-primary',
        label: 'Lima'
      },
      'provider': {
        bg: 'bg-brand-gold/10',
        text: 'text-brand-gold',
        label: 'Proveedor'
      }
    };

    const badge = badges[type] || badges['user'];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold font-montserrat ${badge.bg} ${badge.text}`}>
        {badge.icon && <span>{badge.icon}</span>}
        {badge.label}
      </span>
    );
  }

  return (
    <>
      {/* Modals */}
      <DepositadoModal
        isOpen={showDepositadoModal}
        amount={depositadoAmount}
        onAmountChange={setDepositadoAmount}
        onSave={handleDepositadoSave}
        onCancel={handleDepositadoCancel}
      />

      <ImageUploadModal
        isOpen={showImageModal}
        selectedFiles={selectedFiles}
        previewUrls={previewUrls}
        isUploading={isUploading}
        dragActive={dragActive}
        onClose={handleCloseImageModal}
        onFileSelect={handleFileSelect}
        onDrag={handleDrag}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onRemoveFile={removeFile}
        onUpload={handleUploadImages}
      />

      <ImageViewModal
        isOpen={showViewModal}
        images={viewingImages}
        isLoading={loadingImages}
        onClose={handleCloseViewModal}
        onDelete={handleDeleteImage}
      />

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                Usuario
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                Tipo
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <div className="flex items-center justify-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  <span>Depositar</span>
                </div>
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Depositado</span>
                </div>
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <div className="flex items-center justify-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  <span>Debe</span>
                </div>
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <Upload className="w-4 h-4 mx-auto" title="Subir Imagen" />
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <Eye className="w-4 h-4 mx-auto" title="Ver Imagen" />
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <Calendar className="w-4 h-4 mx-auto" title="Fecha" />
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <FileText className="w-4 h-4 mx-auto" title="Notas" />
              </th>
              <th className="px-3 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                <CreditCard className="w-4 h-4 mx-auto" title="Pago" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                {/* Username */}
                <td className="px-3 py-4 text-sm font-medium text-slate-900 font-montserrat">
                  {item.username}
                </td>

                {/* Type Badge */}
                <td className="px-3 py-4 text-center">
                  {getTypeBadge(item.type)}
                </td>

                {/* Depositar */}
                <td className="px-3 py-4 text-sm text-center font-montserrat font-semibold text-brand-primary">
                  {formatCurrency(item.depositar)}
                </td>

                {/* Depositado */}
                <td className="px-3 py-4 text-center">
                  <button
                    onClick={() => handleDepositadoClick(item)}
                    className={`
                      px-3 py-2 rounded-lg transition-all font-montserrat font-semibold text-sm
                      ${item.depositado > 0
                        ? 'bg-brand-green text-white hover:bg-brand-green/90'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }
                    `}
                    title={item.depositado > 0 ? formatCurrency(item.depositado) : 'Click para ingresar monto'}
                  >
                    {item.depositado > 0 ? formatCurrency(item.depositado) : 'S/ 0.00'}
                  </button>
                </td>

                {/* Debe */}
                <td className="px-3 py-4 text-sm text-center font-montserrat font-semibold text-red-600">
                  {formatCurrency(item.debe)}
                </td>

                {/* Subir Imagen */}
                <td className="px-3 py-4 text-center">
                  <button
                    onClick={() => handleUploadClick(item.id)}
                    className="inline-flex items-center justify-center p-2 bg-brand-gold text-white rounded-lg hover:bg-brand-gold/90 transition-all"
                    title="Subir comprobante"
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                </td>

                {/* Ver Imagen */}
                <td className="px-3 py-4 text-center">
                  <button
                    onClick={() => handleViewImages(item.id)}
                    className={`
                      p-2 rounded-lg transition-all
                      ${(item.image_urls && item.image_urls.length > 0) || item.imagen_url
                        ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
                        : 'bg-slate-200 text-slate-400'
                      }
                    `}
                    title="Ver comprobantes"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>

                {/* Fecha */}
                <td className="px-3 py-4 text-sm text-center text-slate-600 font-montserrat">
                  {item.fecha ? new Date(item.fecha).toLocaleDateString('es-PE') : '-'}
                </td>

                {/* Notas */}
                <td className="px-3 py-4 text-center">
                  {editingNotes === item.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat"
                        placeholder="Notas..."
                        autoFocus
                      />
                      <button
                        onClick={() => handleNotesSave(item.id)}
                        className="p-1 bg-brand-green text-white rounded hover:bg-brand-green/90"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleNotesCancel}
                        className="p-1 bg-slate-300 text-slate-700 rounded hover:bg-slate-400"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNotesClick(item)}
                      className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-all"
                      title={item.notas || 'Agregar notas'}
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                  )}
                </td>

                {/* Pago */}
                <td className="px-3 py-4 text-center">
                  <button
                    onClick={() => handleToggleField(item.id, 'pago', item.pago)}
                    className={`
                      px-3 py-1 rounded-lg text-xs font-bold font-montserrat transition-all
                      ${item.pago
                        ? 'bg-brand-green text-white hover:bg-brand-green/90'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }
                    `}
                  >
                    {item.pago ? 'PAGADO' : 'PENDIENTE'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default PaymentsTable;